import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export function useChat() {
  const [state, setState] = useState(null);
  const [channel, setChannel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const chRef = useRef(channel);
  const lastTyping = useRef(0);
  chRef.current = channel;

  const loadState = useCallback(() => api.get("/chat/state").then((r) => setState(r.data)).catch(() => {}), []);
  const loadMsgs = useCallback(async () => {
    const ch = chRef.current;
    if (!ch) return setMsgs([]);
    try {
      const { data } = await api.get("/chat/messages", { params: { channel: ch } });
      if (chRef.current === ch) setMsgs(data);
    } catch { setMsgs([]); }
  }, []);
  const refresh = useCallback(async () => { await loadMsgs(); await loadState(); }, [loadMsgs, loadState]);

  useEffect(() => { setMsgs([]); refresh(); }, [channel, refresh]);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) refresh(); }, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  const run = async (fn, msg) => {
    try {
      const { data } = await fn();
      await refresh();
      if (msg) toast.success(msg);
      return data || true;
    } catch (e) { err(e); return null; }
  };

  return {
    state, channel, setChannel, msgs, refresh,
    send: (body, ch = channel) => run(() => api.post("/chat/messages", { channel: ch, ...body })),
    edit: (id, text) => run(() => api.patch(`/chat/messages/${id}`, { text }), "Message edited"),
    remove: (id) => run(() => api.delete(`/chat/messages/${id}`), "Message deleted"),
    react: (id, emoji) => run(() => api.post(`/chat/messages/${id}/react`, { emoji })),
    star: (id) => run(() => api.post(`/chat/messages/${id}/star`)),
    track: (id, on) => run(() => api.post(`/chat/messages/${id}/track`), on ? "Read alert turned off" : "We'll alert you when everyone has read it"),
    pinTop: (id, on) => run(() => api.post(`/chat/messages/${id}/pin-top`), on ? "Message unpinned" : "Message pinned to the top"),
    pin: (ch) => run(() => api.post("/chat/pin", { channel: ch })),
    createGroup: (name, members) => run(() => api.post("/chat/groups", { name, members }), "Group created"),
    updateGroup: (id, name, members) => run(() => api.patch(`/chat/groups/${id}`, { name, members }), "Group updated"),
    leaveGroup: (id) => run(() => api.post(`/chat/groups/${id}/leave`), "You left the group"),
    typing: () => {
      if (!channel || Date.now() - lastTyping.current < 3000) return;
      lastTyping.current = Date.now();
      api.post("/chat/typing", { channel }).catch(() => {});
    },
  };
}

const PALETTE = ["bg-amber-100 text-amber-800", "bg-stone-200 text-stone-800", "bg-yellow-100 text-yellow-800", "bg-orange-100 text-orange-800", "bg-zinc-200 text-zinc-800", "bg-lime-100 text-lime-800"];
export const avatarTone = (s = "") => PALETTE[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
export const isOnline = (u) => !!u?.last_seen && Date.now() - new Date(u.last_seen).getTime() < 60000;

export function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export function dayLabel(iso) {
  const d = new Date(iso);
  const t = timeLabel(iso);
  return t === "Yesterday" ? t : d.toDateString() === new Date().toDateString() ? "Today" : d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export function useConversations(state, projects, me) {
  if (!state) return [];
  const users = state.users.filter((u) => u.id !== me);
  const dmKey = (u) => `dm:${[me, u.id].sort().join(":")}`;
  const list = [
    { key: "General", name: "General", type: "channel", sub: "Whole studio" },
    ...projects.map((p) => ({ key: p.name, name: p.name, type: "project", sub: p.client || "Project", project: p })),
    ...state.groups.map((g) => ({ key: `group:${g.id}`, name: g.name, type: "group", sub: `${g.members.length} members`, group: g })),
    ...users.map((u) => ({ key: dmKey(u), name: u.name, type: "dm", sub: "Direct message", user: u })),
  ];
  return list.map((c) => ({ ...c, ...(state.channels[c.key] || { last: null, unread: 0 }), pinned: state.pins.includes(c.key) }))
    .sort((a, b) => (b.pinned - a.pinned) || (b.last?.created_at || "").localeCompare(a.last?.created_at || "") || a.name.localeCompare(b.name));
}
